import { Box, Loader as MantineLoader, LoaderProps, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type LoaderVariant = 'default' | 'gradient' | 'dots' | 'pulse';

export interface ThemedLoaderProps extends Omit<LoaderProps, 'type'> {
  loaderVariant?: LoaderVariant;
  gradientVariant?: GradientVariant;
}

const Loader = forwardRef<HTMLDivElement, ThemedLoaderProps>(
  ({ loaderVariant = 'default', gradientVariant = 'primary', size = 'md', ...rest }, ref) => {
    const theme = useMantineTheme();
    const gradient = theme.other.gradients[gradientVariant];
    const sizeMap: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', number> = {
      xs: 16,
      sm: 22,
      md: 30,
      lg: 40,
      xl: 54,
    };
    const numericSize = typeof size === 'number' ? size : sizeMap[size as keyof typeof sizeMap] || 30;

    if (loaderVariant === 'gradient') {
      return (
        <Box
          ref={ref}
          style={{
            width: numericSize,
            height: numericSize,
            borderRadius: '50%',
            border: `3px solid transparent`,
            borderTopColor: gradient.from,
            borderRightColor: gradient.to,
            animation: 'spin 0.8s linear infinite',
          }}
        >
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </Box>
      );
    }

    if (loaderVariant === 'dots') {
      return (
        <Box ref={ref} style={{ display: 'flex', gap: numericSize / 4 }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              style={{
                width: numericSize / 3,
                height: numericSize / 3,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
                animation: `dot-bounce 1.4s ease-in-out ${i * 0.16}s infinite both`,
              }}
            />
          ))}
          <style>{`
            @keyframes dot-bounce {
              0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
              40% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </Box>
      );
    }

    if (loaderVariant === 'pulse') {
      return (
        <Box
          ref={ref}
          style={{
            width: numericSize,
            height: numericSize,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        >
          <style>{`
            @keyframes pulse {
              0% { transform: scale(0.8); opacity: 1; }
              50% { transform: scale(1.2); opacity: 0.5; }
              100% { transform: scale(0.8); opacity: 1; }
            }
          `}</style>
        </Box>
      );
    }

    return <MantineLoader ref={ref} size={size} color={gradient.from} {...rest} />;
  }
);

Loader.displayName = 'Loader';

export default Loader;
